import React, { useState, useRef } from 'react';
import jsPDF from 'jspdf';
import { CAUSALES, WA_NUMBER, WA_FINIQUITO_MSG } from '../../data/constants';
import { waLink } from '../../utils/whatsapp';
import { calcularFiniquito, formatCLP } from '../../utils/finiquitoCalc';
import { CalculatorIcon } from '../Icons';
import type { FiniquitoResult } from '../../types';
import './Finiquito.css';

const UPLOAD_URL = 'https://us-central1-souza-villa.cloudfunctions.net/uploadFiniquitoDocs';
const MAX_PHOTOS   = 5;
const MAX_PDF_BYTES = 5 * 1024 * 1024; // límite duro exigido para adjuntos: el PDF final nunca debe superar 5 MB
// Combinaciones de [dimensión máx., calidad JPEG] probadas en orden hasta que el PDF pese menos de MAX_PDF_BYTES
const COMPRESSION_STEPS: [number, number][] = [
  [1600, 0.85], [1400, 0.7], [1100, 0.6], [900, 0.5], [700, 0.4], [500, 0.3],
];

const DOC_ITEMS = [
  { key: 'finiquito',     emoji: '📄', title: 'Finiquito',              sub: 'El doc. que le dieron para firmar', multi: false },
  { key: 'liquidaciones', emoji: '💰', title: 'Últimas 3 liquidaciones', sub: 'Sus comprobantes de sueldo',        multi: true  },
  { key: 'afp',           emoji: '🏦', title: 'Cotizaciones AFP',        sub: 'Cartola o comprobante AFP',          multi: false },
  { key: 'carta',         emoji: '✉️', title: 'Carta de despido',        sub: 'Si le entregaron carta formal',      multi: false },
] as const;

type DocKey    = typeof DOC_ITEMS[number]['key'];
type DocEntry  = { blob: Blob; name: string };
type DocStore  = { entries: DocEntry[]; label: string };
type PhotoItem = { name: string; dataUrl: string };

const fileToBase64 = (blob: Blob): Promise<string> =>
  new Promise(resolve => {
    const r = new FileReader();
    r.onload = () => resolve((r.result as string).split(',')[1]);
    r.readAsDataURL(blob);
  });

const readAsDataUrl = (blob: Blob): Promise<string> =>
  new Promise(resolve => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.readAsDataURL(blob);
  });

const loadImg = (src: string): Promise<HTMLImageElement> =>
  new Promise(resolve => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.src = src;
  });

// Arma un PDF multipágina a partir de dataUrls, con una dimensión máx. y calidad JPEG dadas
const buildPdfFromDataUrls = async (dataUrls: string[], maxDim: number, quality: number): Promise<Blob> => {
  const limited = dataUrls.slice(0, MAX_PHOTOS);
  let pdf: jsPDF | null = null;
  for (const dataUrl of limited) {
    const img   = await loadImg(dataUrl);
    const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width  = Math.round(img.naturalWidth  * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
    const jpeg    = canvas.toDataURL('image/jpeg', quality);
    const portrait = canvas.height >= canvas.width;
    if (!pdf) {
      pdf = new jsPDF({ orientation: portrait ? 'portrait' : 'landscape', unit: 'mm', format: 'a4' });
    } else {
      pdf.addPage('a4', portrait ? 'portrait' : 'landscape');
    }
    const pw = pdf.internal.pageSize.getWidth(), ph = pdf.internal.pageSize.getHeight();
    const ratio = Math.min((pw - 10) / canvas.width, (ph - 10) / canvas.height);
    const fw = canvas.width * ratio, fh = canvas.height * ratio;
    pdf.addImage(jpeg, 'JPEG', (pw - fw) / 2, (ph - fh) / 2, fw, fh);
  }
  return pdf!.output('blob');
};

// Combina dataUrls en un solo PDF multipágina, bajando calidad/tamaño hasta pesar menos de 5 MB
const imageDataUrlsToPdf = async (dataUrls: string[]): Promise<Blob> => {
  let blob = await buildPdfFromDataUrls(dataUrls, COMPRESSION_STEPS[0][0], COMPRESSION_STEPS[0][1]);
  for (let i = 1; i < COMPRESSION_STEPS.length && blob.size > MAX_PDF_BYTES; i++) {
    blob = await buildPdfFromDataUrls(dataUrls, COMPRESSION_STEPS[i][0], COMPRESSION_STEPS[i][1]);
  }
  return blob;
};

const Finiquito: React.FC = () => {
  const [causal, setCausal]         = useState('');
  const [fIngreso, setFIngreso]     = useState('');
  const [fAviso, setFAviso]         = useState('');
  const [fTermino, setFTermino]     = useState('');
  const [sueldoBase, setSueldoBase] = useState('');
  const [vacUsadas, setVacUsadas]   = useState('0');
  const [result, setResult]         = useState<FiniquitoResult | null>(null);

  const [docNombre,   setDocNombre]   = useState('');
  const [docEmail,    setDocEmail]    = useState('');
  const [docTelefono, setDocTelefono] = useState('');
  const [store,       setStore]       = useState<Partial<Record<DocKey, DocStore>>>({});
  const [photoQueues, setPhotoQueues] = useState<Partial<Record<DocKey, PhotoItem[]>>>({});
  const [cameraKeys,  setCameraKeys]  = useState<Partial<Record<DocKey, number>>>({});
  const [uploading,   setUploading]   = useState(false);
  const [uploadOk,    setUploadOk]    = useState(false);
  const [uploadError, setUploadError] = useState('');

  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const handleCalc = () => {
    const res = calcularFiniquito({
      causal, fIngreso, fAviso, fTermino,
      sueldoBase: Number(sueldoBase),
      vacUsadas:  Number(vacUsadas) || 0,
    });
    setResult(res);
  };

  const handleFile = (key: DocKey, files: FileList | null, multi: boolean) => {
    if (!files?.length) return;
    const entries: DocEntry[] = Array.from(multi ? files : [files[0]]).map(f => ({ blob: f, name: f.name }));
    const label = entries.length > 1 ? `${entries.length} archivos seleccionados` : entries[0].name;
    setStore(p => ({ ...p, [key]: { entries, label } }));
  };

  // Agrega UNA foto al queue; incrementa cameraKeys para remontar el input y que vuelva a disparar
  const handleCameraCapture = async (key: DocKey, files: FileList | null) => {
    if (!files?.length) return;
    const current = photoQueues[key] || [];
    if (current.length >= MAX_PHOTOS) return;
    const file = files[0]; // capturar referencia antes del await
    const dataUrl = await readAsDataUrl(file);
    setPhotoQueues(prev => ({ ...prev, [key]: [...(prev[key] || []), { name: file.name, dataUrl }] }));
    setCameraKeys(prev => ({ ...prev, [key]: (prev[key] || 0) + 1 }));
  };

  const removeQueuePhoto = (key: DocKey, idx: number) => {
    setPhotoQueues(prev => ({ ...prev, [key]: (prev[key] || []).filter((_, i) => i !== idx) }));
  };

  const enviarDocumentos = async () => {
    const hasFiles  = DOC_ITEMS.some(d => store[d.key]?.entries.length);
    const hasPhotos = DOC_ITEMS.some(d => (photoQueues[d.key] || []).length > 0);
    if (!hasFiles && !hasPhotos) { setUploadError('Adjunte al menos un documento.'); return; }
    if (!docNombre.trim())       { setUploadError('Ingrese su nombre para que podamos contactarle.'); return; }
    setUploading(true); setUploadError('');
    try {
      const files: { fileBase64: string; fileName: string; mimeType: string }[] = [];

      // Archivos adjuntados con el picker: las fotos de un mismo documento se combinan en UN solo PDF
      for (const d of DOC_ITEMS) {
        const entries = store[d.key]?.entries;
        if (!entries?.length) continue;
        const imageEntries = entries.filter(e => e.blob.type.startsWith('image/'));
        const otherEntries = entries.filter(e => !e.blob.type.startsWith('image/'));
        if (imageEntries.length) {
          const dataUrls = await Promise.all(imageEntries.map(e => readAsDataUrl(e.blob)));
          const pdfBlob  = await imageDataUrlsToPdf(dataUrls);
          files.push({ fileBase64: await fileToBase64(pdfBlob), fileName: `${d.key}.pdf`, mimeType: 'application/pdf' });
        }
        for (const entry of otherEntries) {
          files.push({ fileBase64: await fileToBase64(entry.blob), fileName: entry.name, mimeType: entry.blob.type || 'application/octet-stream' });
        }
      }

      // Fotos tomadas con cámara → un PDF por documento
      for (const d of DOC_ITEMS) {
        const queue = photoQueues[d.key] || [];
        if (!queue.length) continue;
        const pdfBlob = await imageDataUrlsToPdf(queue.map(p => p.dataUrl));
        files.push({ fileBase64: await fileToBase64(pdfBlob), fileName: `${d.key}_fotos.pdf`, mimeType: 'application/pdf' });
      }

      const resp = await fetch(UPLOAD_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ files, nombre: docNombre, email: docEmail, telefono: docTelefono }),
      });
      if (!resp.ok) throw new Error();
      setUploadOk(true);
    } catch {
      setUploadError('Error al enviar. Por favor escríbanos por WhatsApp.');
    } finally { setUploading(false); }
  };

  const showAviso = causal === '161';

  return (
    <section id="finiquito" style={{ background: 'var(--dark-2)' }}>
      <div className="section-label">Herramienta Gratuita</div>
      <h2 className="section-title">Calculadora de <em>finiquito</em></h2>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: 1.9, maxWidth: 560, marginTop: '0.4rem', marginBottom: '3rem' }}>
        Estime el monto aproximado de su finiquito según la causal de término y sus datos laborales. Gratis y sin registro.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2.5rem', marginBottom: '3rem' }}>
        {/* ── FORMULARIO ─────────────────────────────── */}
        <div>
          <div style={{ marginBottom: '2rem' }}>
            <div className="finiquito-label-line">
              <span className="finiquito-label-bar" />Seleccione la causal de término
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem', marginTop: '1rem' }}>
              {CAUSALES.map(c => (
                <label key={c.value} className="causal-opt">
                  <input type="radio" name="causal" value={c.value} checked={causal === c.value}
                    onChange={() => setCausal(c.value)} style={{ display: 'none' }} />
                  <div className={`causal-box ${causal === c.value ? 'selected' : ''}`}>
                    <div className="causal-art">{c.art}</div>
                    <div className="causal-name">{c.name}</div>
                    <div className="causal-desc">{c.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="finiquito-datos-box">
            <div className="finiquito-label-line">
              <span className="finiquito-label-bar" />Ingrese sus datos
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.2rem', marginTop: '1.4rem' }}>
              <div className="form-group">
                <label className="form-label">Fecha inicio del contrato</label>
                <input type="date" className="form-control" value={fIngreso} onChange={e => setFIngreso(e.target.value)} />
              </div>
              {showAviso && (
                <div className="form-group">
                  <label className="form-label">Fecha del aviso de despido</label>
                  <input type="date" className="form-control" value={fAviso} onChange={e => setFAviso(e.target.value)} />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Fecha de término del contrato</label>
                <input type="date" className="form-control" value={fTermino} onChange={e => setFTermino(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Su sueldo base mensual ($)</label>
                <input type="number" className="form-control" value={sueldoBase}
                  onChange={e => setSueldoBase(e.target.value)} placeholder="Ej: 600000" min="0" />
              </div>
              <div className="form-group">
                <label className="form-label">Vacaciones que ya tomó (días hábiles)</label>
                <input type="number" className="form-control" value={vacUsadas}
                  onChange={e => setVacUsadas(e.target.value)} placeholder="0" min="0" />
              </div>
            </div>
            <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-primary" onClick={handleCalc} style={{ fontSize: '0.72rem' }}>
                <CalculatorIcon /> Calcular Finiquito →
              </button>
            </div>
          </div>
        </div>

        {/* ── RESULTADO ─────────────────────────────── */}
        <div>
          {!result ? (
            <div className="resultado-vacio">
              <CalculatorIcon size={40} strokeWidth={1} />
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: 'var(--text-muted)', marginTop: '1rem' }}>
                Complete los datos y presione<br />"Calcular Finiquito"
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.8rem', opacity: 0.6 }}>
                El resultado aparecerá aquí
              </div>
            </div>
          ) : (
            <div className="resultado-fin">
              <div className="resultado-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div>
                    <div style={{ fontSize: '0.62rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--gold)' }}>Estimación de Finiquito</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>{result.causalLabel}</div>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'right' }}>{result.antiguedad}</div>
                </div>
                <div style={{ marginTop: '1.2rem', paddingTop: '1.2rem', borderTop: '1px solid rgba(27,58,107,0.1)' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.15em' }}>Total estimado bruto</div>
                  <div className="resultado-total-grande">{formatCLP(result.total)}</div>
                </div>
              </div>

              <div className="resultado-desglose">
                {result.desglose.map((d, i) => (
                  <div className="res-row" key={i}>
                    <div className="res-row-label">
                      {d.label}
                      {d.sublabel && <small>{d.sublabel}</small>}
                    </div>
                    <div className={`res-row-val ${d.value === 0 ? 'zero' : ''}`}>{formatCLP(d.value)}</div>
                  </div>
                ))}
                <div className="res-row total">
                  <div className="res-row-label" style={{ fontWeight: 500, color: 'var(--cream)' }}>Total Estimado</div>
                  <div className="res-row-val total">{formatCLP(result.total)}</div>
                </div>
              </div>

              <div className="resultado-alerta">
                <div style={{ fontSize: '0.78rem', fontWeight: 500, color: 'var(--cream)', marginBottom: '0.5rem' }}>⚠️ Importante — lea antes de usar este número</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-body)', lineHeight: 1.8 }}>
                  El sueldo que usamos para calcular es <strong style={{ color: 'var(--cream)' }}>todo lo que usted recibe todos los meses en forma fija</strong>, sin contar las horas extra.<br /><br />
                  💡 <strong style={{ color: 'var(--cream)' }}>Si recibe gratificación</strong> (un bono por ley o contrato), ese monto puede cambiar el resultado.<br /><br />
                  ⚠️ <strong style={{ color: 'var(--cream)' }}>Este cálculo es una estimación, no es el monto exacto.</strong> Antes de firmar cualquier finiquito, consúltelo con una abogada.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── SECCIÓN DOCS ─────────────────────────────────── */}
      <div className="resultado-docs">
        <div style={{ fontSize: '0.82rem', fontWeight: 500, color: '#6aad6a', marginBottom: '0.4rem' }}>
          ✅ ¿Prefiere que lo revisemos gratis?
        </div>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: '1.2rem' }}>
          Adjunte los documentos que tenga (no es obligatorio tenerlos todos) y nuestras abogadas revisarán su caso sin costo.
        </div>

        {/* Datos de contacto — 3 cols en desktop, 1 col en móvil */}
        <div className="contact-grid">
          <div className="form-group">
            <label className="form-label">Su nombre *</label>
            <input className="form-control" value={docNombre} onChange={e => setDocNombre(e.target.value)} placeholder="Nombre completo" />
          </div>
          <div className="form-group">
            <label className="form-label">Correo</label>
            <input className="form-control" type="email" value={docEmail} onChange={e => setDocEmail(e.target.value)} placeholder="correo@ejemplo.com" />
          </div>
          <div className="form-group">
            <label className="form-label">Teléfono</label>
            <input className="form-control" value={docTelefono} onChange={e => setDocTelefono(e.target.value)} placeholder="+56 9..." />
          </div>
        </div>

        {/* Grilla de documentos — 2 cols en desktop, 1 en móvil */}
        <div className="doc-grid">
          {DOC_ITEMS.map(doc => {
            const queue = photoQueues[doc.key] || [];
            const atMax = queue.length >= MAX_PHOTOS;
            return (
              <div className="doc-upload-item" key={doc.key}>
                {/* Input adjuntar archivo */}
                <input
                  type="file"
                  ref={el => { inputRefs.current[`${doc.key}_adj`] = el; }}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  multiple={doc.multi}
                  style={{ display: 'none' }}
                  onChange={e => { handleFile(doc.key, e.target.files, doc.multi); e.target.value = ''; }}
                />
                {/* Input cámara: key cambia tras cada foto para forzar remount y permitir otro disparo */}
                <input
                  key={`${doc.key}_foto_${cameraKeys[doc.key] || 0}`}
                  type="file"
                  ref={el => { inputRefs.current[`${doc.key}_foto`] = el; }}
                  accept="image/*"
                  capture="environment"
                  style={{ display: 'none' }}
                  onChange={e => { handleCameraCapture(doc.key, e.target.files); }}
                />

                <div style={{ fontSize: '1.2rem', marginBottom: '0.3rem' }}>{doc.emoji}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--cream)', fontWeight: 500, marginBottom: '0.2rem' }}>{doc.title}</div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginBottom: '0.6rem' }}>{doc.sub}</div>

                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <button type="button" className="doc-btn-adj"
                    onClick={() => inputRefs.current[`${doc.key}_adj`]?.click()}>
                    📎 Adjuntar
                  </button>
                  <button
                    type="button"
                    className={`doc-btn-foto${atMax ? ' doc-btn-disabled' : ''}`}
                    disabled={atMax}
                    onClick={() => inputRefs.current[`${doc.key}_foto`]?.click()}>
                    📷 {queue.length > 0 ? `Foto (${queue.length}/${MAX_PHOTOS})` : 'Foto'}
                  </button>
                </div>

                {/* Miniaturas de fotos en cola */}
                {queue.length > 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                      {queue.map((p, i) => (
                        <div key={i} style={{ position: 'relative' }}>
                          <img src={p.dataUrl} alt=""
                            style={{ width: 36, height: 36, objectFit: 'cover', border: '1px solid rgba(27,58,107,0.3)' }} />
                          <button
                            type="button"
                            onClick={() => removeQueuePhoto(doc.key, i)}
                            style={{
                              position: 'absolute', top: -5, right: -5,
                              background: '#c0504a', color: 'white', border: 'none',
                              borderRadius: '50%', width: 15, height: 15,
                              fontSize: 10, cursor: 'pointer', padding: 0,
                              lineHeight: '15px', textAlign: 'center',
                            }}>×</button>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
                      {atMax
                        ? `Máximo ${MAX_PHOTOS} fotos alcanzado`
                        : `${queue.length} foto${queue.length > 1 ? 's' : ''} — se compilarán en un PDF`}
                    </div>
                  </div>
                )}

                {/* Archivo adjuntado vía picker */}
                {store[doc.key] && (
                  <div style={{ fontSize: '0.65rem', color: 'var(--gold)', marginTop: '0.3rem', wordBreak: 'break-all' }}>
                    ✓ {store[doc.key]!.label}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {uploadOk ? (
          <div style={{ textAlign: 'center', padding: '1rem', color: '#6aad6a', fontSize: '0.85rem', fontWeight: 500 }}>
            ✅ Documentos enviados con éxito. Nos contactaremos a la brevedad.
          </div>
        ) : (
          <>
            <button type="button" className="btn-primary" onClick={enviarDocumentos} disabled={uploading}
              style={{ fontSize: '0.72rem', width: '100%', justifyContent: 'center', marginBottom: '0.5rem' }}>
              {uploading ? 'Enviando…' : 'Enviar documentos y pedir revisión gratuita →'}
            </button>
            {uploadError && (
              <div style={{ fontSize: '0.72rem', color: '#c0504a', textAlign: 'center', marginBottom: '0.5rem' }}>{uploadError}</div>
            )}
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textAlign: 'center' }}>
              Solo cargue los que tenga. No es obligatorio tenerlos todos.
            </div>
          </>
        )}

        <div style={{ marginTop: '0.8rem' }}>
          <a href={waLink(WA_FINIQUITO_MSG)} target="_blank" rel="noopener noreferrer"
            className="btn-ghost" style={{ fontSize: '0.7rem', width: '100%', justifyContent: 'center' }}>
            Prefiero escribir por WhatsApp →
          </a>
        </div>
      </div>
    </section>
  );
};

export default Finiquito;
